import React, { FunctionComponent, useEffect, useState } from "react";
import MaterialTable from "@material-table/core";
import {
  StoreOrder,
  StoreOrderItem,
  StoreOrderItemType,
  StoreOrderState
} from "../../../../models/Store";
import { OrderUnit } from "../../../../models/Order";
import { Product } from "../../../../models/Product";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "../../../../store";
import { thunkFetchProductsV2 } from "../../../../store/products/action";
import {
  Dialog,
  DialogContent,
  Typography,
  IconButton,
  Button,
  TextField,
  Snackbar,
  InputAdornment,
  useTheme,
  useMediaQuery,
  FormControl,
  MenuItem,
  Select,
  InputLabel
} from "@material-ui/core";

import Alert from "@material-ui/lab/Alert";

import useStyles from "../styles";
import MuiDialogTitle from "@material-ui/core/DialogTitle";
import CloseIcon from "@material-ui/icons/Close";
import { thunkFetchStoreInventoryItems } from "../../../../store/stores/actions";
import { ConfirmDialog } from "../../../../components/dialogs/ConfirmDialog";
import { StoreInventoryItemsState } from "../../../../store/stores/types";

import * as StoreOrderApi from "../../../../api/storeOrderApi";
import { Autocomplete } from "@material-ui/lab";
import { parseNumber } from "../../../../utils";
import { useI18n } from "../../../../hooks/useI18n";

interface State {
  editOrderItemDialogOpen: boolean;
  showRemoveOrderItem: boolean;
  deleteItemFailed: boolean;
  showAddDialog: boolean;
  updateItemSuccess: boolean;
  updateItemFailed: boolean;
  addItemFailed: boolean;
  error?: any;
  order: StoreOrder;
  data: StoreOrderItem[];
  orderItemToEdit?: any;
  orderItemToAdd?: StoreOrderItem;
  orderItemIdToDelete?: string;
  loading: boolean;
}

interface OrdersTableProps {
  order: StoreOrder;
  onAdd?: (item: StoreOrderItem) => void;
}

export const OrderItemsTable: FunctionComponent<OrdersTableProps> = ({
  order
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { t } = useI18n();

  const products =
    useSelector<AppState, ReadonlyArray<Product>>(
      s => s.storeClusterProducts.productState.products
    ) || [];

  const inventoryState = useSelector<AppState, StoreInventoryItemsState>(
    s => s.storeInventoryItems
  );

  let { storeInventoryItems } = inventoryState;

  const [state, setState] = useState<State>({
    editOrderItemDialogOpen: false,
    showRemoveOrderItem: false,
    showAddDialog: false,
    updateItemSuccess: false,
    updateItemFailed: false,
    deleteItemFailed: false,
    addItemFailed: false,
    order: order,
    loading: false,
    data: []
  });

  useEffect(() => {
    dispatch(thunkFetchProductsV2());
    dispatch(thunkFetchStoreInventoryItems(order.stores[0].id));
  }, []);

  useEffect(() => {
    setState({ ...state, loading: true });
    StoreOrderApi.getOrder(order.id).then(result => {
      if (result.type === "OK") {
        const { value } = result;
        setState({
          ...state,
          data: value.items && value.items.length ? value.items.slice() : [],
          loading: false
        });
      }
    });
  }, [order.id]);

  const getAddProductOptions = () => {
    const val =
      products &&
      products.length &&
      storeInventoryItems &&
      storeInventoryItems.length &&
      state.data &&
      state.data.length > 0
        ? products.filter(
            p =>
              p.orderInfo &&
              state.order?.distributors?.find(
                d => d.id === p.orderInfo?.distributor?.id
              ) &&
              storeInventoryItems.find(si => si.productId === p.id) &&
              !state.data.find((j: any) => j.product.id === p.id)
          )
        : [];
    return val;
  };

  return (
    <>
      <MaterialTable
        title={""}
        columns={[
          {
            title: "GTIN",
            field: "product.gtin",
            hidden: true,
            searchable: true
          },
          {
            title: "ArticleNumber",
            field: "product.articleNumber",
            hidden: true,
            searchable: true
          },
          {
            title: "Brand",
            field: "product.brandName",
            hidden: true,
            searchable: true
          },
          {
            title: "Product",
            field: "product.name",
            render: (row: StoreOrderItem) => {
              return (
                <div>
                  <Typography>{row.product.name}</Typography>
                  <Typography color="textSecondary">
                    {row.product.gtin}, {row.product.size}{" "}
                    {row.product.brandName}
                    {row.product.articleNumber
                      ? `, artNo: ${row.product.articleNumber}`
                      : ""}
                  </Typography>
                </div>
              );
            }
          },
          {
            title: "Category",
            field: "product.category.name"
          },
          {
            title: "Quantity",
            field: "quantity",
            render: (row: StoreOrderItem) => {
              return `${row.quantity} ${
                row.unit === OrderUnit.Kfp
                  ? "ST/KFP"
                  : `DFP (${row.distributionPackageSize ?? "..."} st)`
              }`;
            }
          },

          {
            title: "Currently on shelf",
            field: "onShelfBeforeDelivery"
          },
          {
            title: "Delivered",
            field: "delivered",
            render: (row: StoreOrderItem) => {
              return row.delivered ? "Yes" : "No";
            }
          },
          {
            title: "Carry",
            field: "statistics.carry"
          }
        ]}
        data={state.data}
        isLoading={state.loading}
        actions={[
          {
            icon: "add_circle",
            disabled: state.order.state === StoreOrderState.Ordered,
            iconProps: {
              style: {
                color:
                  state.order.state === StoreOrderState.Ordered
                    ? undefined
                    : "rgb(31, 132, 52)",
                fontSize: "2rem"
              }
            },
            tooltip:
              "Add new order item (can only be done when state is 'Proposed' or 'Confirmed')",
            isFreeAction: true,
            onClick: () => setState({ ...state, showAddDialog: true })
          },
          {
            icon: "edit",
            iconProps: { style: { color: "rgba(0, 0, 0, 0.54)" } },
            tooltip: t("common.actions.editItem"),
            onClick: (event, rowData: any) => {
              setState({
                ...state,
                editOrderItemDialogOpen: true,
                orderItemToEdit: {
                  id: rowData.id,
                  quantity: rowData.quantity,
                  unit: rowData.unit
                }
              });
            }
          },
          {
            icon: "delete",
            iconProps: { style: { color: "rgba(0, 0, 0, 0.54)" } },
            tooltip: t("common.actions.removeItem"),
            onClick: (event, rowData: any) =>
              setState({
                ...state,
                showRemoveOrderItem: true,
                orderItemToEdit: {
                  id: rowData.id
                }
              })
          }
        ]}
        options={{
          pageSize: 10,
          pageSizeOptions: [20, 30, 50],
          actionsColumnIndex: -1,
          actionsCellStyle: { whiteSpace: "nowrap" }
        }}
        localization={{
          header: {
            actions: ""
          }
        }}
      />
      <Dialog
        fullWidth={true}
        maxWidth={"sm"}
        open={state.editOrderItemDialogOpen}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <MuiDialogTitle disableTypography>
          <IconButton
            aria-label="close"
            className={classes.closeButton}
            onClick={() => {
              setState({
                ...state,
                editOrderItemDialogOpen: false
              });
            }}
          >
            <CloseIcon />
          </IconButton>
        </MuiDialogTitle>
        <DialogContent style={{ marginTop: 10 }}>
          <TextField
            className={classes.field}
            label="Quantity"
            value={state?.orderItemToEdit?.quantity}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <FormControl className={classes.field} variant={"standard"}>
                    <InputLabel id="order-unit-select-label">
                      Order unit
                    </InputLabel>
                    <Select
                      labelId="order-unit-select-label"
                      value={state?.orderItemToEdit?.unit ?? ""}
                      onChange={e =>
                        setState({
                          ...state,
                          orderItemToEdit: {
                            ...state.orderItemToEdit,
                            unit: e.target.value
                              ? (e.target.value as OrderUnit)
                              : undefined
                          }
                        })
                      }
                    >
                      <MenuItem key={"Kfp"} value={OrderUnit.Kfp}>
                        Kfp
                      </MenuItem>
                      <MenuItem key={"Dfp"} value={OrderUnit.Dfp}>
                        Dfp
                      </MenuItem>
                    </Select>
                  </FormControl>
                </InputAdornment>
              )
            }}
            onChange={e => {
              setState({
                ...state,
                orderItemToEdit: {
                  ...state.orderItemToEdit,
                  quantity: e.target.value
                }
              });
            }}
          />
          <div
            style={{ display: "flex", justifyContent: "center", marginTop: 20 }}
          >
            <Button
              style={{ width: 200 }}
              variant="contained"
              color="primary"
              disabled={
                !state.orderItemToEdit ||
                !state.orderItemToEdit.quantity ||
                state.orderItemToEdit.quantity <= 0
              }
              onClick={async () => {
                const { orderItemToEdit } = state;

                const result = await StoreOrderApi.updateItem(
                  orderItemToEdit?.id,
                  orderItemToEdit
                );

                if (result.type === "ERROR") {
                  setState({
                    ...state,
                    updateItemFailed: true,
                    error: result.details.detail
                  });
                } else {
                  setState({
                    ...state,
                    data: state.data.map(item => {
                      if (item.id === orderItemToEdit.id) {
                        item.quantity = orderItemToEdit?.quantity;
                        item.unit = orderItemToEdit?.unit;
                      }
                      return item;
                    })
                  });
                  setState({
                    ...state,
                    updateItemSuccess: true,
                    editOrderItemDialogOpen: false
                  });
                }
              }}
            >
              Save item
            </Button>
          </div>
          {state.updateItemFailed && (
            <div
              style={{
                display: "flex",
                marginTop: 20,
                justifyContent: "center"
              }}
            >
              <Alert severity={"error"}>{state.error}</Alert>
            </div>
          )}
        </DialogContent>
      </Dialog>
      <Dialog
        fullWidth={true}
        maxWidth={"sm"}
        open={state.showAddDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <MuiDialogTitle disableTypography>
          <IconButton
            aria-label="close"
            className={classes.closeButton}
            onClick={() => {
              setState({
                ...state,
                showAddDialog: false,
                orderItemToAdd: undefined
              });
            }}
          >
            <CloseIcon />
          </IconButton>
        </MuiDialogTitle>
        <DialogContent>
          <div>
            <Autocomplete
              id="products-auto-complete"
              autoComplete
              includeInputInList
              className={classes.field}
              renderInput={params => (
                <TextField {...params} label="Product *" margin="normal" />
              )}
              options={getAddProductOptions().slice()}
              getOptionLabel={(product: Product) => {
                let rendered = "";

                if (product.brandName && product.brandName !== "...") {
                  rendered += product.brandName;
                }

                if (product.name && product.name !== "...") {
                  if (rendered) {
                    rendered += ", ";
                  }

                  rendered += product.name;
                }

                if (product.details?.size && product.details?.size !== "...") {
                  if (rendered) {
                    rendered += ", ";
                  }

                  rendered += product.details?.size;
                }

                return rendered;
              }}
              onChange={(evt, value) => {
                const product = value as Product;
                const inventoryItem =
                  storeInventoryItems && storeInventoryItems.length
                    ? storeInventoryItems.find(
                        si => si.productId === product.id
                      )
                    : undefined;

                let toAdd: StoreOrderItem = {
                  id: "00000000-0000-0000-0000-000000000000", // just as dummy id.. later replaced when new database id
                  product: {
                    id: product.id,
                    name: product.name,
                    price: product.pricing.price,
                    size: product.details?.size,
                    brandName: product.brandName,
                    gtin: product.gtin,
                    category: {
                      id: product.category.id,
                      name: product.category.name
                    },
                    priceUnit: product.pricing.priceUnit,
                    maxOnShelf: product.orderInfo?.maxOnShelf ?? -1,
                    orderThreshold: product.orderInfo?.orderThreshold ?? -1
                  },
                  quantity: 0,
                  onShelfBeforeDelivery: 0,
                  type: StoreOrderItemType.ManuallyAdded,
                  unit:
                    inventoryItem?.orderUnit ??
                    product.orderInfo?.orderUnit ??
                    OrderUnit.Dfp
                };

                setState({
                  ...state,
                  orderItemToAdd: toAdd
                });
              }}
            />
          </div>
          {state.orderItemToAdd && (
            <>
              <TextField
                className={classes.field}
                label="Quantity *"
                type="number"
                value={state.orderItemToAdd?.quantity ?? ""}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <FormControl
                        className={classes.field}
                        variant={"standard"}
                      >
                        <InputLabel id="order-unit-select-label">
                          Order unit
                        </InputLabel>
                        <Select
                          labelId="order-unit-select-label"
                          value={state?.orderItemToAdd?.unit ?? ""}
                          onChange={e => {
                            if (state.orderItemToAdd) {
                              setState({
                                ...state,
                                orderItemToAdd: {
                                  ...state.orderItemToAdd,
                                  unit: e.target.value
                                    ? (e.target.value as OrderUnit)
                                    : OrderUnit.Dfp
                                }
                              });
                            }
                          }}
                        >
                          <MenuItem key={"Kfp"} value={OrderUnit.Kfp}>
                            Kfp
                          </MenuItem>
                          <MenuItem key={"Dfp"} value={OrderUnit.Dfp}>
                            Dfp
                          </MenuItem>
                        </Select>
                      </FormControl>
                    </InputAdornment>
                  )
                }}
                onChange={e => {
                  if (state.orderItemToAdd) {
                    setState({
                      ...state,
                      orderItemToAdd: {
                        ...state.orderItemToAdd,
                        quantity: parseNumber(e.target.value) ?? 0
                      }
                    });
                  }
                }}
              />

              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  marginTop: 20
                }}
              >
                <Button
                  style={{ width: 200 }}
                  variant="contained"
                  color="primary"
                  disabled={
                    state.orderItemToAdd && state.orderItemToAdd?.quantity <= 0
                  }
                  onClick={async () => {
                    const result = await StoreOrderApi.addItem(order.id, {
                      productId: state.orderItemToAdd?.product.id,
                      quantity: state.orderItemToAdd?.quantity,
                      quantityUnit: state.orderItemToAdd?.unit
                    });
                    if (result.type === "ERROR") {
                      setState({ ...state, addItemFailed: true });
                    } else {
                      if (!state.orderItemToAdd) {
                        return;
                      }
                      const items = state.data;
                      items.push(state.orderItemToAdd);
                      setState({
                        ...state,
                        data: items,
                        showAddDialog: false,
                        orderItemToAdd: undefined
                      });
                    }
                  }}
                >
                  Add item
                </Button>
              </div>
            </>
          )}
          {state.addItemFailed && (
            <div
              style={{
                display: "flex",
                marginTop: 20,
                justifyContent: "center"
              }}
            >
              <Alert severity="error">{state.error}</Alert>
            </div>
          )}
        </DialogContent>
      </Dialog>
      <ConfirmDialog
        visible={state.showRemoveOrderItem}
        titleText={`Remove`}
        bodyText={`Are you sure you want to remove the product from the order?`}
        confirmText={"Remove"}
        onConfirm={async () => {
          const result = await StoreOrderApi.deleteItem(
            state.orderItemToEdit.id
          );
          if (result.type === "ERROR") {
            setState({
              ...state,
              deleteItemFailed: true,
              error: result.details.detail
            });
          } else {
            setState({
              ...state,
              showRemoveOrderItem: false,
              data: state.data.filter(
                item => item.id != state.orderItemToEdit.id
              )
            });
          }
          return true;
        }}
        onCancel={() => {
          setState({ ...state, showRemoveOrderItem: false });
        }}
      />
      <Snackbar
        id={"priceTags"}
        open={state.updateItemSuccess}
        autoHideDuration={6000}
        onClose={() =>
          setState({ ...state, updateItemSuccess: false, error: undefined })
        }
      >
        <Alert severity="success">
          {t("common.messages.itemHasBeenUpdated")}
        </Alert>
      </Snackbar>
      <Snackbar
        id={"priceTags"}
        open={state.deleteItemFailed}
        autoHideDuration={6000}
        onClose={() =>
          setState({ ...state, deleteItemFailed: false, error: undefined })
        }
      >
        <Alert severity={"error"}>{state.error}</Alert>
      </Snackbar>
    </>
  );
};
